import { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { ApplyVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';

/**
 * Shared apply/measure logic for effect-block editors, so simple panel blocks
 * (fade, flip, look, speed…) don't each re-implement the boilerplate.
 *
 * Returns measured media dims (from PreviewCanvas onMeta), the aspect ratio,
 * apply/error state, and an `apply({ type, params, extraMedia, startTime,
 * endTime }, onApplied)` that builds the block + media info and POSTs to
 * ApplyBlocks. Timing defaults to the whole clip.
 */
export function useBlockApply({ videoWorkspaceId, sourceFileName, mediaInfo }) {
    const [measured, setMeasured] = useState(() => (
        Number(mediaInfo?.width) > 0 && Number(mediaInfo?.height) > 0 ? mediaInfo : null
    ));
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const ready = Number(measured?.width) > 0 && Number(measured?.height) > 0;

    const apply = async ({ type, params, extraMedia, startTime = 0, endTime = null }, onApplied) => {
        if (!videoWorkspaceId || !sourceFileName || !ready) {
            setError('Make sure the clip has loaded.');
            return false;
        }
        setApplying(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const block = { id: `${type}-1`, type, source: sourceFileName, startTime, endTime, params };
            const media = {
                [sourceFileName]: {
                    width: Math.round(Number(measured.width)),
                    height: Math.round(Number(measured.height)),
                    duration: Number(measured.duration) || 0,
                },
                ...(extraMedia || {}),
            };
            const res = await ApplyVideoWorkspaceBlocks(idToken, videoWorkspaceId, [block], media);
            if (res.status === 200) {
                onApplied?.(res.myJson);
                return true;
            }
            setError(res.myJson?.detail || 'Failed to apply the effect.');
            return false;
        } catch (err) {
            setError('Error applying edit: ' + err.message);
            return false;
        } finally {
            setApplying(false);
        }
    };

    return { measured, setMeasured, aspectRatio, ready, applying, error, setError, apply };
}
